import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { Suspense } from 'react';
import { multiFactor } from 'firebase/auth';
import { z } from 'zod';
import { AppShell } from '@/components/app-shell';
import { auth } from '@/lib/firebase';
import { lazyWithReload } from '@/lib/lazy-with-reload';
import { readActiveOrgId, writeActiveOrgId } from '@/lib/active-org';
import type { OrgRole, PlatformRole } from '@jam/shared';

const SignInPage = lazyWithReload(() =>
  import('@/pages/sign-in').then((m) => ({ default: m.SignInPage })),
);
const SignUpPage = lazyWithReload(() =>
  import('@/pages/sign-up').then((m) => ({ default: m.SignUpPage })),
);
const ForgotPasswordPage = lazyWithReload(() =>
  import('@/pages/forgot-password').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazyWithReload(() =>
  import('@/pages/reset-password').then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const VerifyEmailPage = lazyWithReload(() =>
  import('@/pages/verify-email').then((m) => ({ default: m.VerifyEmailPage })),
);
const VerifyEmailSentPage = lazyWithReload(() =>
  import('@/pages/verify-email-sent').then((m) => ({
    default: m.VerifyEmailSentPage,
  })),
);
const MfaEnrollPage = lazyWithReload(() =>
  import('@/pages/mfa-enroll').then((m) => ({ default: m.MfaEnrollPage })),
);
const MfaChallengePage = lazyWithReload(() =>
  import('@/pages/mfa-challenge').then((m) => ({
    default: m.MfaChallengePage,
  })),
);
const CommandCenterPage = lazyWithReload(() =>
  import('@/pages/command-center').then((m) => ({ default: m.CommandCenterPage })),
);
const DossierPage = lazyWithReload(() =>
  import('@/pages/dossier').then((m) => ({ default: m.DossierPage })),
);

const ComparePage = lazyWithReload(() =>
  import('@/pages/compare').then((m) => ({ default: m.ComparePage })),
);

const TodosPage = lazyWithReload(() =>
  import('@/pages/todos').then((m) => ({ default: m.TodosPage })),
);

const MapPage = lazyWithReload(() =>
  import('@/pages/map').then((m) => ({ default: m.MapPage })),
);
const GraphPage = lazyWithReload(() =>
  import('@/pages/graph').then((m) => ({ default: m.GraphPage })),
);
const InvestigationsPage = lazyWithReload(() =>
  import('@/pages/investigations').then((m) => ({ default: m.InvestigationsPage })),
);
const InvestigationDetailPage = lazyWithReload(() =>
  import('@/pages/investigation-detail').then((m) => ({
    default: m.InvestigationDetailPage,
  })),
);
const BriefComposerPage = lazyWithReload(() =>
  import('@/pages/brief').then((m) => ({ default: m.BriefComposerPage })),
);
const BriefViewPage = lazyWithReload(() =>
  import('@/pages/brief-view').then((m) => ({ default: m.BriefViewPage })),
);
const EntityResolutionPage = lazyWithReload(() =>
  import('@/pages/entity-resolution').then((m) => ({
    default: m.EntityResolutionPage,
  })),
);
const AskTheCupQueryPage = lazyWithReload(() =>
  import('@/pages/query').then((m) => ({ default: m.AskTheCupQueryPage })),
);
const CapitalFlowsPage = lazyWithReload(() =>
  import('@/pages/capital-flows').then((m) => ({ default: m.CapitalFlowsPage })),
);
const DataSourcesPage = lazyWithReload(() =>
  import('@/pages/data-sources').then((m) => ({ default: m.DataSourcesPage })),
);
const PublicBriefPage = lazyWithReload(() =>
  import('@/pages/public-brief').then((m) => ({ default: m.PublicBriefPage })),
);
const AdminSettingsPage = lazyWithReload(() =>
  import('@/pages/admin-settings').then((m) => ({
    default: m.AdminSettingsPage,
  })),
);
const SuperAdminPage = lazyWithReload(() =>
  import('@/pages/superadmin').then((m) => ({ default: m.SuperAdminPage })),
);
const SuperAdminOrgsNewPage = lazyWithReload(() =>
  import('@/pages/superadmin-orgs-new').then((m) => ({
    default: m.SuperAdminOrgsNewPage,
  })),
);
const SuperAdminUsersPage = lazyWithReload(() =>
  import('@/pages/superadmin-users').then((m) => ({
    default: m.SuperAdminUsersPage,
  })),
);
const SuperAdminAuditPage = lazyWithReload(() =>
  import('@/pages/superadmin-audit').then((m) => ({
    default: m.SuperAdminAuditPage,
  })),
);
const OrgSettingsPage = lazyWithReload(() =>
  import('@/pages/org-settings').then((m) => ({ default: m.OrgSettingsPage })),
);
const OrgIngestPage = lazyWithReload(() =>
  import('@/pages/org-ingest').then((m) => ({ default: m.OrgIngestPage })),
);
const OrgMembersPage = lazyWithReload(() =>
  import('@/pages/org-members').then((m) => ({ default: m.OrgMembersPage })),
);
const OrgInvitesPage = lazyWithReload(() =>
  import('@/pages/org-invites').then((m) => ({ default: m.OrgInvitesPage })),
);
const InviteAcceptPage = lazyWithReload(() =>
  import('@/pages/invite-accept').then((m) => ({ default: m.InviteAcceptPage })),
);
const OnboardingSelectOrgPage = lazyWithReload(() =>
  import('@/pages/onboarding-select-org').then((m) => ({
    default: m.OnboardingSelectOrgPage,
  })),
);
const OnboardingNoOrgPage = lazyWithReload(() =>
  import('@/pages/onboarding-no-org').then((m) => ({
    default: m.OnboardingNoOrgPage,
  })),
);

const LoadingFallback = () => (
  <div className="flex h-full items-center justify-center text-sm text-[var(--color-ink-muted)]">
    Loading…
  </div>
);

const rootRoute = createRootRoute({
  component: () => (
    <Suspense fallback={<LoadingFallback />}>
      <Outlet />
    </Suspense>
  ),
});

/**
 * Read platformRole + orgs claim from the user's ID token. Duplicated
 * (short) here + in auth-provider so the router guard can run BEFORE
 * the provider has mounted — the token is the source of truth either way.
 */
function readClaims(claims: Record<string, unknown>): {
  platformRole: PlatformRole | null;
  orgsClaim: Record<string, OrgRole>;
} {
  const rawPlatform = claims['platformRole'];
  const platformRole: PlatformRole | null =
    rawPlatform === 'superAdmin' ? 'superAdmin' : null;
  const rawOrgs = claims['orgs'];
  const orgsClaim: Record<string, OrgRole> = {};
  if (rawOrgs && typeof rawOrgs === 'object') {
    for (const [id, role] of Object.entries(
      rawOrgs as Record<string, unknown>,
    )) {
      if (role === 'admin' || role === 'member') orgsClaim[id] = role;
    }
  }
  return { platformRole, orgsClaim };
}

async function requireAuth() {
  await auth.authStateReady();
  const current = auth.currentUser;
  if (!current) {
    throw redirect({ to: '/sign-in' });
  }
  const isPasswordProvider = current.providerData.some(
    (p) => p.providerId === 'password',
  );
  if (isPasswordProvider && !current.emailVerified) {
    throw redirect({ to: '/verify-email-sent' });
  }

  // MFA is required for every tenant member in the multi-tenant world.
  // Skipped for the emulator — see below.
  const inEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';
  const tokenResult = await current.getIdTokenResult();
  const { orgsClaim } = readClaims(tokenResult.claims);
  const hasAnyMembership = Object.keys(orgsClaim).length > 0;

  if (!inEmulator && hasAnyMembership) {
    const enrolled = multiFactor(current).enrolledFactors.length > 0;
    if (!enrolled) {
      throw redirect({ to: '/mfa-enroll' });
    }
  }

  // Membership gate: zero-org users get a helpful dead-end; multi-org
  // users without a pinned active org get the onboarding picker.
  if (!hasAnyMembership) {
    throw redirect({ to: '/onboarding/no-org' });
  }
  const stored = readActiveOrgId();
  const storedIsValid = stored != null && orgsClaim[stored] !== undefined;
  if (!storedIsValid) {
    // Try to auto-pick if there's only one org.
    const ids = Object.keys(orgsClaim);
    if (ids.length === 1 && ids[0]) {
      writeActiveOrgId(ids[0]);
    } else {
      throw redirect({ to: '/onboarding/select-org' });
    }
  }
}

async function requireSuperAdmin() {
  await auth.authStateReady();
  const current = auth.currentUser;
  if (!current) throw redirect({ to: '/sign-in' });
  const tokenResult = await current.getIdTokenResult();
  const { platformRole } = readClaims(tokenResult.claims);
  if (platformRole !== 'superAdmin') {
    throw redirect({ to: '/' });
  }
}

async function requireOrgAdmin() {
  await auth.authStateReady();
  const current = auth.currentUser;
  if (!current) throw redirect({ to: '/sign-in' });
  const tokenResult = await current.getIdTokenResult();
  const { platformRole, orgsClaim } = readClaims(tokenResult.claims);
  if (platformRole === 'superAdmin') return;
  const stored = readActiveOrgId();
  const role = stored ? orgsClaim[stored] : undefined;
  if (role !== 'admin') {
    throw redirect({ to: '/' });
  }
}

async function requireMembership() {
  // Less strict than requireAuth — the onboarding/select-org page itself
  // sits outside the authed shell but still needs a signed-in user with
  // at least one org.
  await auth.authStateReady();
  const current = auth.currentUser;
  if (!current) throw redirect({ to: '/sign-in' });
  const tokenResult = await current.getIdTokenResult();
  const { orgsClaim } = readClaims(tokenResult.claims);
  if (Object.keys(orgsClaim).length === 0) {
    throw redirect({ to: '/onboarding/no-org' });
  }
}

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignInPage,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-up',
  component: SignUpPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const oobCodeSearch = z.object({ oobCode: z.string() });

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
  validateSearch: (search) => oobCodeSearch.parse(search),
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  component: VerifyEmailPage,
  validateSearch: (search) => oobCodeSearch.parse(search),
});

const verifyEmailSentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email-sent',
  component: VerifyEmailSentPage,
});

async function requireUserForEnroll() {
  await auth.authStateReady();
  if (!auth.currentUser) throw redirect({ to: '/sign-in' });
}

const mfaEnrollRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mfa-enroll',
  beforeLoad: requireUserForEnroll,
  component: MfaEnrollPage,
});

const mfaChallengeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mfa-challenge',
  component: MfaChallengePage,
});

const publicBriefRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/b/$token',
  component: PublicBriefPage,
});

// Invite acceptance — deliberately OUTSIDE the authed tree so an
// unsigned-in prospect can see the preview. Accept CTA bounces through
// sign-in then back.
const inviteAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: InviteAcceptPage,
});

// Onboarding surfaces — gated by signed-in user only (not requireAuth)
// so they don't loop on themselves when there's no active org yet.
async function requireSignedIn() {
  await auth.authStateReady();
  if (!auth.currentUser) throw redirect({ to: '/sign-in' });
}

const onboardingSelectOrgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/select-org',
  beforeLoad: requireMembership,
  component: OnboardingSelectOrgPage,
});

const onboardingNoOrgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/no-org',
  beforeLoad: requireSignedIn,
  component: OnboardingNoOrgPage,
});

const authedSearch = z.object({
  demo: z
    .union([z.literal('1'), z.literal('true'), z.literal(1), z.boolean()])
    .optional(),
});

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: requireAuth,
  component: AppShell,
  validateSearch: (search) => authedSearch.parse(search),
});

const commandCenterSearch = z.object({ q: z.string().optional() });

const commandCenterRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: CommandCenterPage,
  validateSearch: (search) => commandCenterSearch.parse(search),
});

const graphSearch = z.object({
  preset: z.enum(['parsi-bridge']).optional(),
  // Chat-driven deep-link: ?focus=<entity-name>&hops=2 puts the page in
  // focus mode on load. Used by the dispatchChatAction `focusSubgraph`
  // verb so a "show me Parsi's network" turn lands the analyst in the
  // right view in one navigation. `hops` is optional; the page falls
  // back to its default when absent.
  focus: z.string().optional(),
  hops: z.coerce.number().int().min(1).max(4).optional(),
});

const graphRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/graph',
  component: GraphPage,
  validateSearch: (search) => graphSearch.parse(search),
});

const dossierRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/entity/$name',
  component: DossierPage,
});

const compareSearch = z.object({
  a: z.string().optional(),
  b: z.string().optional(),
});

const compareRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/compare',
  component: ComparePage,
  validateSearch: (search) => compareSearch.parse(search),
});

const todosRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/todos',
  component: TodosPage,
});

const mapRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/map',
  component: MapPage,
});

const investigationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/investigations',
  component: InvestigationsPage,
});

const investigationDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/investigations/$id',
  component: InvestigationDetailPage,
});

const briefSearch = z.object({
  entity: z.string().optional(),
  fromInvestigation: z.string().optional(),
});

const briefRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/brief',
  component: BriefComposerPage,
  validateSearch: (search) => briefSearch.parse(search),
});

const briefViewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/brief/$id',
  component: BriefViewPage,
});

const entityResolutionRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/er',
  component: EntityResolutionPage,
});

const askTheCupQueryRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/query',
  component: AskTheCupQueryPage,
});

const capitalFlowsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/capital',
  component: CapitalFlowsPage,
});

const dataSourcesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sources',
  component: DataSourcesPage,
});

// The admin-settings hub (graph grouping, data import, Neo4j health) touches
// platform-wide resources: the Neo4j graph is shared across tenants, the
// grouping config is global, and the signals cron is a global orchestrator.
// Gated on superAdmin so it matches the server-side guard on /admin/*
// endpoints (which were promoted to superAdmin for the same reason).
const adminSettingsSearch = z.object({
  section: z.enum(['graph', 'import', 'system', 'jobs']).optional(),
  // Optional ingest-run id — links from the run-finished card expand the
  // matching row in the recent-runs table without spawning a new route.
  runId: z.string().optional(),
});

const adminSettingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/settings',
  beforeLoad: requireSuperAdmin,
  component: AdminSettingsPage,
  validateSearch: (search) => adminSettingsSearch.parse(search),
});

// --- Org self-service -----------------------------------------------------

const orgSettingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/org',
  beforeLoad: requireOrgAdmin,
  component: OrgSettingsPage,
});

const orgMembersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/org/members',
  beforeLoad: requireOrgAdmin,
  component: OrgMembersPage,
});

const orgInvitesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/org/invites',
  beforeLoad: requireOrgAdmin,
  component: OrgInvitesPage,
});

const orgIngestRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/org/ingest',
  beforeLoad: requireOrgAdmin,
  component: OrgIngestPage,
});

// --- Platform superadmin --------------------------------------------------

const superAdminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/superadmin',
  beforeLoad: requireSuperAdmin,
  component: SuperAdminPage,
});

const superAdminOrgsNewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/superadmin/orgs/new',
  beforeLoad: requireSuperAdmin,
  component: SuperAdminOrgsNewPage,
});

const superAdminUsersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/superadmin/users',
  beforeLoad: requireSuperAdmin,
  component: SuperAdminUsersPage,
});

const superAdminAuditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/superadmin/audit',
  beforeLoad: requireSuperAdmin,
  component: SuperAdminAuditPage,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  signUpRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  verifyEmailRoute,
  verifyEmailSentRoute,
  mfaEnrollRoute,
  mfaChallengeRoute,
  publicBriefRoute,
  inviteAcceptRoute,
  onboardingSelectOrgRoute,
  onboardingNoOrgRoute,
  authedRoute.addChildren([
    commandCenterRoute,
    graphRoute,
    dossierRoute,
    compareRoute,
    todosRoute,
    mapRoute,
    investigationsRoute,
    investigationDetailRoute,
    briefRoute,
    briefViewRoute,
    entityResolutionRoute,
    askTheCupQueryRoute,
    capitalFlowsRoute,
    dataSourcesRoute,
    adminSettingsRoute,
    orgSettingsRoute,
    orgMembersRoute,
    orgInvitesRoute,
    orgIngestRoute,
    superAdminRoute,
    superAdminOrgsNewRoute,
    superAdminUsersRoute,
    superAdminAuditRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
